import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'common/useDebounce';
import { SetStateAction, useAtom, WritableAtom } from 'jotai';
import { Autocomplete } from 'common/components/Inputs/Autocomplete/Autocomplete';
import { useProductCodesAutocomplete } from './hooks/useProductCodesAutocomplete';

const MAX_PRODUCT_CODES_TO_FILTER = 10;

type ProductCodeSearchFilterProps = {
  productCodesFilterAtom: WritableAtom<
    (string | null)[],
    [SetStateAction<(string | null)[]>],
    void
  >;
  productCodesHintsAtom: WritableAtom<string[], [SetStateAction<any>], void>;
};

export const ProductCodeSearchFilter = ({
  productCodesFilterAtom,
  productCodesHintsAtom,
}: ProductCodeSearchFilterProps) => {
  const { t } = useTranslation();
  const [productCodes, setProductCodes] = useAtom(productCodesFilterAtom);
  const [hints, removeHints] = useAtom(productCodesHintsAtom);

  const [productCodeSearch, setProductCodeSearch] = useState('');

  const debounceProductCode = useDebounce(productCodeSearch, 500);
  const blankValuesEnabled = !debounceProductCode.length;

  const {
    options,
    hintOptions,
    selectedOptions,
    isFetched,
    hasNextPage,
    fetchNextPage,
    updateSelectedOptions,
  } = useProductCodesAutocomplete({
    search: debounceProductCode,
    initialIds: productCodes,
    hints: hints,
    onSelectedChange: (options) => {
      setProductCodes(options.map(({ id }) => id));
    },
  });

  const hasMaxProductCodesLimitsOccurs =
    productCodes && productCodes.length >= MAX_PRODUCT_CODES_TO_FILTER;
  const errors = useMemo(() => {
    let errors: string[] = [];
    if (hasMaxProductCodesLimitsOccurs) {
      errors = [t('common.limitReached')];
    }

    return errors;
  }, [hasMaxProductCodesLimitsOccurs]);

  return (
    <Autocomplete
      data-testid="product-codes-multiselect"
      placeholder={t('rSimInventory.productCodesFilterPlaceholder')}
      value={selectedOptions}
      options={options}
      hints={hintOptions}
      onChange={updateSelectedOptions}
      onSearch={setProductCodeSearch}
      onHintRemove={removeHints}
      skeletonRepeats={productCodes.length}
      blankValueEnabled={blankValuesEnabled}
      loading={!isFetched}
      errors={errors}
      disallowSelecting={hasMaxProductCodesLimitsOccurs}
      disallowSelectingReason={t('common.limitReached')}
      hasMore={hasNextPage}
      onMoreShouldBeLoad={fetchNextPage}
    />
  );
};
